<template>
  <div class="text-black tab-content">
    <!-- ************************ -->
    <!-- card type settings begin -->
    <!-- ************************ -->
    <v-col class="flex-wrap" cols="12">
      <div class="mt-4 mb-3 d-flex align-center" style="font-size: 32px">
        Card Type Settings
        <span>
          <help-dialog-card-type-settings />
        </span>
      </div>
    </v-col>

    <v-col class="flex-wrap" cols="12">
      <div>
        <v-row class="flex-wrap align-center pl-1 mb-0 pb-2" style="border-bottom: 1px solid var(--light-3)">
          <v-col cols="1">
          </v-col>
          <v-col cols="2" class="text-left">
            Type name
          </v-col>
          <v-col cols="2" class="text-center">
            Show in backlog
          </v-col>
          <v-col cols="1" class="text-center text-no-wrap">
            Use AI
            <help-dialog-workflow-cards :settings="true" />
          </v-col>
          <v-col cols="5" class="text-center text-no-wrap">
            Domain Model Role
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-icon size="21" :color="infoIconColor" v-bind="props">mds:info</v-icon>
              </template>
              <span>
                Pick if this card type should be used as a Command, Aggregate Root, Read Model
                or Given-When-Then in the 'Domain Model' section.
              </span>
            </v-tooltip>
          </v-col>
          <v-col class="text-left">
          </v-col>
        </v-row>

        <div id="card-types" ref="cardTypes">
          <workflow-settings-card-type
            class="sortableRow"
            v-for="cardType in cardTypes"
            :key="cardType.id"
            :data-id="cardType.id"
            :cardType="cardType"
            :disabled="$store.state.isWorkflowDisabled"
          />
        </div>

        <div class="d-flex align-center justify-space-between">
          <div id="default-type" class="d-flex flex-column gap-y-1 mt-6">
            <span>
              Default Type
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-icon size="20.5" v-bind="props">mdi-information-outline</v-icon>
                </template>
                <span>Default card type for new cards. Only enabled for types with SHOW IN BACKLOG selected.</span>
              </v-tooltip>
            </span>
            <v-select
              density="compact"
              variant="outlined"
              :items="cardTypes.filter(rt => rt.showInBacklog)"
              v-model="defaultCardType"
              @update:model-value="handleDefaultTypeChange"
              return-object
              :disabled="$store.state.isWorkflowDisabled"
            >
              <template #item="{ item, props }">
                <div v-bind="props" class="d-flex align-center p-3 cursor-pointer">
                  <workflow-settings-card-type-color-picker
                    :color="item.raw.color && item.raw.color.rgb"
                    variant="cards"
                    :disabled="true"
                  />
                  <span class="ml-2">{{ item.raw.title }}</span>
                </div>
              </template>
              <template #selection="{ item }">
                <workflow-settings-card-type-color-picker
                  :color="item.raw.color && item.raw.color.rgb"
                  variant="cards"
                  :disabled="true"
                ></workflow-settings-card-type-color-picker>
                <span class="ml-2">{{ item.raw.title }}</span>
              </template>
            </v-select>
          </div>
          <div class="d-flex justify-end mt-2 mr-3">
            <v-btn rounded="lg" size="large" variant="flat" color="primary" @click="createCardType()"
                   :disabled="$store.state.isWorkflowDisabled">Add Type
            </v-btn>
          </div>
        </div>
      </div>
    </v-col>
    <!-- ********************** -->
    <!-- card type settings end -->
    <!-- ********************** -->
  </div>
</template>

<script>
import Sortable from 'sortablejs'
import midString from '@/utils/mid-string'
import WorkflowSettingsCardType from '@/components/WorkflowSettingsCardType'
import WorkflowSettingsCardTypeColorPicker from '@/components/WorkflowSettingsCardTypeColorPicker'
import store from '@/store'
import workflowApi from '@/api/workflowApi'
import cardTypesApi from '@/api/cardTypesApi'
// import throttle from '@/utils/throttle'
import debounce from '@/utils/debounce'
import { mapState } from 'vuex'
import { isDarkTheme } from '@/composables/useAppTheme'
import HelpDialogWorkflowCards from '@/components/HelpDialogWorkflowCards'
import HelpDialogCardTypeSettings from '@/components/HelpDialogCardTypeSettings'

export default {
  components: {
    WorkflowSettingsCardType,
    WorkflowSettingsCardTypeColorPicker,
    HelpDialogWorkflowCards,
    HelpDialogCardTypeSettings
  },

  computed: {
    ...mapState({
      workflow: state => state.workflow.workflow,
      cardTypes: state => state.cardTypes.cardTypes
    }),
    infoIconColor () {
      return isDarkTheme() ? '#FFFFFF9e' : '#0000009e'
    }
  },

  data () {
    return {
      aiVersion: 0,
      createTypeOpen: false,
      createTypeFetching: false,
      validForm: true,
      isLoading: true,
      rules: {
        required: value => !!value || 'Required.'
      },
      saveAiDescriptionDebouncer: debounce(e => {
        this.saveAiDescription(e)
      }, 1000),
      defaultCardType: undefined
    }
  },

  methods: {
    debouncedSaveAiDescription ({ description, skipDebounce }) {
      if (skipDebounce) {
        this.saveAiDescriptionDebouncer.cancel()
        this.saveAiDescription(description)
      } else {
        this.saveAiDescriptionDebouncer(description)
      }
    },
    saveAiDescription (e) {
      workflowApi.updateWorkflowNoHistoryMutation(
        this.$route.params.projectId,
        {
          id: this.$route.params.id,
          expectedVersion: this.workflow.version,
          aiDescription: e
        }
      )
    },
    async createCardType () {
      let counter = 1
      while (this.cardTypes.findIndex(rt => rt.title === `Card type ${counter}`) >= 0) {
        counter++
      }
      const title = `Card type ${counter}`
      const description = `Card type ${counter} description`
      const color = {
        red: 158,
        green: 158,
        blue: 158
      }
      const workflowId = this.$route.params.id
      this.createTypeFetching = true
      const input = {
        title,
        description,
        color,
        showInBacklog: true,
        domainModelRole: null,
        defaultType: false,
        order: midString(this.cardTypes[this.cardTypes.length - 1]?.order || '', '')
      }
      await store.dispatch('cardTypes/createCardType', { workflowId, input })
      this.createTypeOpen = false
      this.createTypeFetching = false
    },
    initSortable () {
      this.sortable = new Sortable(this.$refs.cardTypes, {
        animation: 150,
        draggable: '.sortableRow',
        handle: '.sortHandle',
        ghostClass: 'sortable-ghost', // Class name for the drop placeholder
        chosenClass: 'sortable-chosen', // Class name for the chosen item
        dragClass: 'sortable-drag',
        scroll: true,
        fallbackOnBody: true,
        forceFallback: false,
        scrollSensitivity: 100,
        dataIdAttr: 'data-id',
        onStart: function (event) {
          Array.prototype.forEach.call(
            document.querySelectorAll('.v-row-group__header'),
            function (el, index) {
              if (!el.classList.contains('sortableRow') && index !== 0) {
                el.classList.add('sortableRow')
              }
            }
          )
          Array.prototype.forEach.call(
            document.querySelectorAll('.sortableRow:not(.sortable-chosen)'),
            function (el, index) {
              if (!el.classList.contains('white-background-on-hover') && index !== 0) {
                el.classList.add('white-background-on-hover')
              }
            }
          )
        },
        onEnd: this.handleEndDrag
      })
    },
    async handleEndDrag (event) {
      let midOrder = ''
      const count = this.cardTypes.length

      // Validate `cardTypes` array
      if (!Array.isArray(this.cardTypes) || count === 0) {
        console.error('Invalid cardTypes array.')
        return
      }

      // Check if orders need initialization
      const isInit = this.cardTypes.some(rt => rt.order === null)
      let newOrders = Array(count).fill('')

      if (isInit) {
        // Initialize orders
        newOrders[0] = midString('', '')
        for (let i = 1; i < count; ++i) {
          newOrders[i] = midString(newOrders[i - 1], '')
        }
      } else {
        newOrders = this.cardTypes.map(rt => rt.order)
      }

      // Determine movement direction
      const isMovingDown = event.newIndex > event.oldIndex

      // Calculate new order
      if (isMovingDown) {
        const nextOrder = event.newIndex + 1 < count ? newOrders[event.newIndex + 1] : ''
        midOrder = midString(newOrders[event.newIndex], nextOrder)
      } else {
        const prevOrder = event.newIndex > 0 ? newOrders[event.newIndex - 1] : ''
        midOrder = midString(prevOrder, newOrders[event.newIndex])
      }

      // Update the moved item's order
      newOrders[event.oldIndex] = midOrder

      // Update card types via API
      if (isInit) {
        // Update all items if orders were initialized
        for (let i = 0; i < count; ++i) {
          await cardTypesApi.updateCardType(this.$route.params.id, this.cardTypes[i], {
            order: newOrders[i]
          })
        }
      } else {
        // Update only the moved item
        await cardTypesApi.updateCardType(this.$route.params.id, this.cardTypes[event.oldIndex], {
          order: midOrder
        })
      }
    },
    async handleDefaultTypeChange (newDefaultType) {
      const workflowId = this.$route.params.id

      for (const rt of this.cardTypes) {
        if (rt.id === newDefaultType.id && !rt.defaultType) {
          try {
            await cardTypesApi.updateCardType(workflowId, rt, { defaultType: true })
          } catch (error) {
            this.$store.commit('snackbar/showMessage', {
              content: 'Error while updating the default card type.',
              color: 'red'
            }, { root: true })
          }
        }
      }
    }
  },

  mounted () {
    this.initSortable()

    this.defaultCardType = this.cardTypes.find((rt) => rt.defaultType)
  }
}
</script>

<style scoped>
.tab-content {
  height: 900px;
}

:deep(.v-select__menu-icon) {
  display: none !important;
}

.sortableRow > div:focus-within {
  outline: 2px solid #4d90fe;
}

.sortable-chosen {
  background: #eee;
}

.sortable-ghost {
  background: #E5E3E1;
}

.sortable-ghost > div {
  opacity: 0.01;
}

[data-theme="dark"] .sortable-chosen,
[data-theme="dark"] .sortable-ghost {
  background: var(--light-3);
}

.p-3 {
  padding: 12px;
}

.gap-y-1 {
  row-gap: 8px;
}

#default-type {
  span {
    font-weight: 600;
  }
}
</style>
